/**
 * Platform bootstrap (contract §4, DNKADM-0274). `env.journixApiBaseUrl` (the
 * derived api origin) is the only host this app may compute; every other
 * platform host — login, auth, brand, asset, pay — is a singleton anchored to
 * the tenant apex that this app cannot derive. This module asks the product
 * API for them once, at app start, and holds the answer **in memory only**:
 * no `localStorage`/`sessionStorage`/cookie/service worker, no TTL, no
 * refresh timer. Persisting is actively harmful — a cached `loginUrl` would
 * outlive an operator changing a tenant's hostnames, making durable exactly
 * the failure this contract prevents. Staleness is already bounded
 * server-side (300s).
 *
 * `GET {apiBase}/bootstrap` is **unauthenticated** by design — it tells an
 * unauthenticated visitor where to log in — so it is issued with `fetch`
 * (never the configured axios instance, whose interceptors assume a resolved
 * auth base; using it here to resolve the auth base would be circular) and
 * `credentials: 'omit'` explicitly.
 */
import { env } from './env';

export interface Bootstrap {
  tenantSlug: string | null;
  tenantName: string | null;
  tenantStatus: string | null;
  /** Canonical, never null/blank, carries NO query string. Required — a
   *  bootstrap response missing it is a failure, not a partial success. */
  loginUrl: string;
  /** Required — see `loginUrl`. */
  authOrigin: string;
  brandOrigin: string | null;
  assetOrigin: string | null;
  payOrigin: string | null;
  /** The member app's origin (`DNKAPI-0507`), used verbatim, never derived
   *  from this console's own hostname (that inversion is `env.memberOrigin`,
   *  DNKADM-0283, and is off-limits here — see `NoConsoleAccess`). Null until
   *  the platform resolves one for the tenant; consumers must degrade rather
   *  than guess. */
  memberOrigin: string | null;
  product: { slug: string | null; displayName: string | null } | null;
}

/**
 * A resolved bootstrap failure. `status` is the HTTP status when the request
 * reached the server — a `404` means this hostname is registered to no
 * tenant (an operator-provisioning issue) — or `null` for a transport-level
 * failure (network error, malformed body). The startup gate uses it to tell
 * a provisioning failure from a transient one.
 */
export class BootstrapError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'BootstrapError';
    this.status = status;
  }
}

let resolved: Bootstrap | null = null;

/**
 * Resolve bootstrap from the product API and cache it in memory. Throws
 * `BootstrapError` on any failure — there is never a composed fallback; no
 * certificate exists for one.
 */
export async function resolveBootstrap(): Promise<Bootstrap> {
  let response: Response;
  try {
    response = await fetch(`${env.journixApiBaseUrl}/bootstrap`, { credentials: 'omit' });
  } catch {
    throw new BootstrapError('Could not reach the platform bootstrap endpoint.', null);
  }
  if (!response.ok) {
    throw new BootstrapError(
      `Bootstrap request failed with status ${response.status}.`,
      response.status,
    );
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new BootstrapError('Bootstrap response was not valid JSON.', response.status);
  }
  const parsed = parseBootstrap(body);
  if (!parsed) {
    throw new BootstrapError(
      'Bootstrap response is missing a required field (loginUrl or authOrigin).',
      response.status,
    );
  }
  resolved = parsed;
  return parsed;
}

/**
 * The resolved bootstrap. Throws if called before `resolveBootstrap()` has
 * succeeded — there is no default, placeholder, or composed fallback. The
 * startup gate guarantees nothing renders (and so nothing calls this) before
 * bootstrap resolves; this throw is the guard if that ever changes.
 */
export function getBootstrap(): Bootstrap {
  if (!resolved) {
    throw new Error('getBootstrap() called before bootstrap resolved.');
  }
  return resolved;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function parseProduct(value: unknown): Bootstrap['product'] {
  if (!value || typeof value !== 'object') return null;
  const p = value as Record<string, unknown>;
  return { slug: nullableString(p.slug), displayName: nullableString(p.displayName) };
}

/**
 * Parse the bootstrap body defensively — every field but `loginUrl` and
 * `authOrigin` degrades to `null` on a wrong/missing type; nothing is ever
 * parsed, rebuilt, normalized, or stripped/appended — origins are carried
 * verbatim. Returns `null` (a failure) when either required field is missing
 * or blank.
 */
function parseBootstrap(body: unknown): Bootstrap | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const loginUrl = typeof b.loginUrl === 'string' && b.loginUrl.length > 0 ? b.loginUrl : null;
  const authOrigin =
    typeof b.authOrigin === 'string' && b.authOrigin.length > 0 ? b.authOrigin : null;
  if (!loginUrl || !authOrigin) return null;
  return {
    tenantSlug: nullableString(b.tenantSlug),
    tenantName: nullableString(b.tenantName),
    tenantStatus: nullableString(b.tenantStatus),
    loginUrl,
    authOrigin,
    brandOrigin: nullableString(b.brandOrigin),
    assetOrigin: nullableString(b.assetOrigin),
    payOrigin: nullableString(b.payOrigin),
    memberOrigin: nullableString(b.memberOrigin),
    product: parseProduct(b.product),
  };
}

/** Test seam — seed or clear the resolved bootstrap without a real fetch. */
export function __setBootstrapForTest(bootstrap: Bootstrap | null): void {
  resolved = bootstrap;
}
